import React, { useState } from "react";
import {
  Table,
  Tag,
  Space,
  Button,
  Drawer,
  Descriptions,
  Typography,
  Card,
} from "antd";
import { LoadingScreen, NoAccessScreen } from "../../components";
import { AppFeature } from "../../Constant";
import { usePagePermission } from "../../Hook";
import { useQuery } from "@tanstack/react-query";
import { FetchSystemLogs_api } from "../../Api";
import { Eye, History, User } from "lucide-react";
import { truncate } from "../../lib";

const { Title, Text } = Typography;

export const SystemLogs = () => {
  const { isLoading: permissionLoading, hasPermission } = usePagePermission(
    AppFeature.SYSTEM_LOGS,
    ["admin", "cashier", "manager"],
  );

  const [selectedLog, setSelectedLog] = useState<any>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Pagination & Search
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [searchText, setSearchText] = useState("");

  const {
    data: logsData,
    isLoading: isLoadingLogs,
    refetch,
  } = useQuery({
    queryKey: ["systemLogs", page, pageSize, searchText],
    queryFn: () => FetchSystemLogs_api(page, pageSize, searchText),
    refetchOnWindowFocus: true,
  });

  const getActionColor = (action: string) => {
    const act = action ? action.toUpperCase() : "";
    if (act.includes("DELETE")) return "error";
    if (act.includes("UPDATE") || act.includes("EDIT")) return "warning";
    if (act.includes("ADD") || act.includes("SAVE") || act.includes("CREATE"))
      return "success";
    if (act.includes("LOGIN")) return "blue";
    if (act.includes("PROCESS")) return "purple";
    return "default";
  };

  const columns = [
    {
      title: "Timestamp",
      dataIndex: "created_at",
      key: "created_at",
      width: 180,
      render: (date: string) => (
        <Space size="middle">
          <History size={14} className="text-gray-400" />
          <Text className="text-gray-600 text-sm">{date}</Text>
        </Space>
      ),
    },
    {
      title: "User",
      dataIndex: "username",
      key: "username",
      width: 150,
      render: (name: string) => (
        <Space>
          <User size={14} className="text-blue-500" />
          <Text strong>{name || "System"}</Text>
        </Space>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: 150,
      render: (action: string) => (
        <Tag color={getActionColor(action)} className="uppercase font-semibold">
          {action}
        </Tag>
      ),
    },
    {
      title: "Details",
      dataIndex: "details",
      key: "details",
      render: (details: string) => (
        <Text ellipsis={{ tooltip: details }}>{truncate(details,100)}</Text>
      ),
    },
    {
      title: "View",
      key: "view",
      width: 80,
      align: "center" as const,
      render: (record: any) => (
        <Button
          type="link"
          icon={<Eye size={18} />}
          onClick={() => {
            setSelectedLog(record);
            setDrawerOpen(true);
          }}
        />
      ),
    },
  ];

  if (permissionLoading) {
    return <LoadingScreen messageQueue={"Loading logs..."} />;
  }

  if (!hasPermission) {
    return <NoAccessScreen />;
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="flex justify-between items-center px-6">
        <h1 className="text-2xl font-semibold">System Logs</h1>
      </div>

      <Card className="shadow-sm overflow-hidden">
        <Table
          dataSource={logsData?.logs || []}
          columns={columns}
          rowKey="id"
          loading={isLoadingLogs}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: logsData?.total || 0,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
            showSizeChanger: true,
          }}
          className="rounded-lg overflow-x-scroll"
        />
      </Card>

      <Drawer
        title={
          <Space>
            <History size={20} />
            <span>Log Details - ID: {selectedLog?.id}</span>
          </Space>
        }
        width={600}
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
      >
        {selectedLog && (
          <Space direction="vertical" size="large" className="w-full">
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Action">
                <Tag color={getActionColor(selectedLog.action)}>
                  {selectedLog.action}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="User">
                {selectedLog.username || "System"}
              </Descriptions.Item>
              <Descriptions.Item label="Timestamp">
                {selectedLog.created_at}
              </Descriptions.Item>
              <Descriptions.Item label="Details">
                {selectedLog.details}
              </Descriptions.Item>
            </Descriptions>

            <div className="mt-4">
              <Title level={5}>Request Payload</Title>
              <div className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-auto max-h-[400px]">
                <pre className="text-xs">
                  {selectedLog.payload
                    ? JSON.stringify(JSON.parse(selectedLog.payload), null, 2)
                    : "No payload available"}
                </pre>
              </div>
            </div>
          </Space>
        )}
      </Drawer>
    </div>
  );
};
